import {
  useCallback,
  type ChangeEvent,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';

import { Gtm } from '@/domains/core/tracking/utils/Gtm';
import { CANDIDATE_INDEX_EXTENSION } from '@/domains/productDiscovery/algolia/constants';
import { getIndexes } from '@/domains/productDiscovery/algolia/settings/settings.common';
import { sortTranslations } from '@/domains/productDiscovery/Listings/components/Filters/translations';

import { useCurrentIndex } from '../../../hooks/useCurrentIndex';
import { useRefine } from '../../../hooks/useRefine';
import type { Item } from '../components/RadioSelect/interfaces';

const onSortChangeTracking = (label: string) => {
  Gtm.push({
    event: 'interaction_click on sort option',
    e_category: 'navigation',
    e_action: 'Click on sort option',
    event_trigger: label,
    amp_event_name: 'Click on sort option',
  });
};

const getIndexExtension = (index: string) => {
  if (index.includes(CANDIDATE_INDEX_EXTENSION)) {
    return CANDIDATE_INDEX_EXTENSION;
  }
  return '';
};

export const useSortFilter = (callback?: () => void) => {
  const currentIndex = useCurrentIndex();
  const refine = useRefine();

  const indexes = getIndexes();
  const currentIndexExtension = getIndexExtension(currentIndex || '');

  const sortItems: Item[] = indexes.map((index) => ({
    label: sortTranslations[index.urlKey],
    active: index.value === currentIndex?.replace(currentIndexExtension, ''),
    value: index.value,
  }));

  const handleSortFilterSelection = useCallback(
    (
      _event:
        | Event
        | ChangeEvent<HTMLInputElement>
        | KeyboardEvent
        | MouseEvent,
      item: Item,
    ) => {
      const urlKey = indexes.find(
        (index) => index.value === item.value,
      )?.urlKey;

      onSortChangeTracking(item.label);
      refine({
        sort: urlKey !== 'relevance' ? urlKey : undefined,
        page: undefined,
      });

      if (callback) callback();
    },
    [indexes, refine, callback],
  );

  const activeValue = sortItems.find((item) => item.active);

  return {
    sortItems,
    handleSortFilterSelection,
    activeLabel: activeValue?.label,
  };
};
